/**
 * <InterruptedResumePanel>
 *
 * The single interrupted-visit recovery UI — a fixed full-height right-side
 * rail, styled to match the `<ZoomPanel>` call rail (same #0f172a body, square
 * edges, left seam border, anchored below the chart's sticky chrome) so
 * resuming feels continuous with the call panel that replaces it. Auto-appears
 * so the doctor doesn't have to click New Encounter → Virtual Visit. Replaces
 * the retired centered `InterruptedVisitModal`. Presentational only; the host
 * owns the action wiring + busy state. Rendered on two surfaces:
 *
 *   - **Patient chart** (`PatientPage`), driven by provider `interruptedSession`.
 *     Resume / Finish navigate to `/call/:encId?autoJoin=1&intent=…` (the host
 *     wires this — the chart can't host a call); Discard runs inline via the
 *     provider.
 *   - **`/call` route** (`CallPage`), driven by the recovery hook's gate state.
 *     Resume / Finish run in-place (the call surface owns the rejoin + finalize
 *     wiring); Discard goes through the hook.
 */
import { useZoomPanelWidthReservation } from './useZoomPanelWidthReservation'
import {
  capturedDurationLabel,
  lastActivityLabel,
  type CapturedSessionLike,
} from './interruptedVisitLabels'
import { CHART_CONTENT_HEIGHT_VAR, CHART_CONTENT_TOP_VAR } from '@/pages/Patient/chartTopOffset'

const CORAL = '#e8624a'

/**
 * Rail width. A little wider than the call `<ZoomPanel>` rail
 * (DEFAULT_PANEL_WIDTH = 272) so the recovery prompt reads as the focal point.
 */
const PANEL_WIDTH = 320

/** Which busy action (if any) is currently in flight — drives spinners + disables. */
export type InterruptedResumeBusy = null | 'resume' | 'finish' | 'discard'

export type InterruptedResumePanelProps = {
  /**
   * Structural — accepts both the provider's `InterruptedSession` (chart
   * surface) and the recovery hook's `ActiveScribeSession` (the `/call`
   * surface). The card only reads `chunkCount` + the label-helper fields.
   */
  session: CapturedSessionLike
  /** Which action is mid-flight; null when idle. */
  busy: InterruptedResumeBusy
  /**
   * Degrade state. When true the call already ended (dead room), so the card
   * hides "Resume" and offers Finish / Discard only. On the chart this is
   * normally false — the dead-room degrade happens on `/call` after a Resume
   * navigation — but the prop is kept for parity with the modal.
   */
  callEnded: boolean
  onResume: () => void
  onFinish: () => void
  onDiscard: () => void
}

export function InterruptedResumePanel({
  session,
  busy,
  callEnded,
  onResume,
  onFinish,
  onDiscard,
}: InterruptedResumePanelProps) {
  const capturedLabel = capturedDurationLabel(session)
  const lines = session.chunkCount
  const anyBusy = busy !== null

  // Reserve the rail's footprint via the shared `--zoom-panel-width` hook. It
  // ref-counts, so the reservation stays correct even if this panel and
  // <ZoomPanel> briefly overlap on a transition — they no longer clobber it.
  useZoomPanelWidthReservation(PANEL_WIDTH)

  return (
    <div
      data-testid="interrupted-resume-panel"
      // Match the <ZoomPanel> call rail: full-height right-side rail, #0f172a
      // body, square edges, left seam border, anchored below the chart's sticky
      // chrome. Width = PANEL_WIDTH; the effect above reserves the same width so
      // the chart reflows beside it rather than being covered.
      className="fixed flex flex-col overflow-y-auto bg-[#0f172a] text-slate-200"
      style={{
        top: CHART_CONTENT_TOP_VAR,
        right: 0,
        width: PANEL_WIDTH,
        height: CHART_CONTENT_HEIGHT_VAR,
        borderLeft: '1px solid #1e293b',
        // Above chart content (insurance-card images etc. were overlapping the
        // buttons when this fell back to `z-index: auto`), but below Mantine's
        // modal overlay (200) so the Discard confirm sits on top.
        zIndex: 100,
      }}
    >
      <div className="p-6 text-center">
        <div className="mb-2 text-[16px] font-bold text-slate-100">
          {callEnded ? 'This call has already ended' : 'Interrupted visit found'}
        </div>

        {callEnded ? (
          <div className="mb-5 text-[12.5px] leading-[1.65] text-slate-400">
            We couldn’t rejoin the video call — it looks like it already ended. You can still
            generate a note from the transcript that was captured.
          </div>
        ) : (
          <div className="mb-4 text-[12.5px] leading-[1.65] text-slate-400">
            A scribe session for this visit is still running. It looks like the call was interrupted
            before it was finished.
          </div>
        )}

        {/* Captured-so-far summary chips. */}
        <div
          className="mb-5 rounded-[10px] border border-white/10 bg-[#1e293b] px-4 py-3 text-left"
          data-testid="interrupted-resume-panel-summary"
        >
          <div className="flex items-center justify-between py-[2px]">
            <span className="text-[11.5px] text-slate-500">Captured</span>
            <span className="text-[12.5px] font-semibold text-slate-200 tabular-nums">
              {capturedLabel}
            </span>
          </div>
          <div className="flex items-center justify-between py-[2px]">
            <span className="text-[11.5px] text-slate-500">Transcript</span>
            <span className="text-[12.5px] font-semibold text-slate-200 tabular-nums">
              {lines} {lines === 1 ? 'line' : 'lines'}
            </span>
          </div>
          <div className="flex items-center justify-between py-[2px]">
            <span className="text-[11.5px] text-slate-500">Last activity</span>
            <span className="text-[12.5px] font-semibold text-slate-200">
              {lastActivityLabel(session)}
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          {!callEnded && (
            <button
              type="button"
              onClick={onResume}
              disabled={anyBusy}
              data-testid="interrupted-resume-panel-resume"
              className="w-full rounded-lg border-0 p-[10px] text-[13px] font-bold text-white disabled:cursor-not-allowed disabled:opacity-60"
              style={{ background: CORAL }}
            >
              {busy === 'resume' ? 'Resuming…' : 'Resume call'}
            </button>
          )}

          <button
            type="button"
            onClick={onFinish}
            disabled={anyBusy}
            data-testid="interrupted-resume-panel-finish"
            className={
              callEnded
                ? 'w-full rounded-lg border-0 p-[10px] text-[13px] font-bold text-white disabled:cursor-not-allowed disabled:opacity-60'
                : 'w-full rounded-lg border border-white/15 bg-transparent p-[9px] text-[12.5px] font-semibold text-slate-200 hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-60'
            }
            style={callEnded ? { background: CORAL } : undefined}
          >
            {busy === 'finish' ? 'Generating note…' : 'Finish & generate note'}
          </button>

          <button
            type="button"
            onClick={onDiscard}
            disabled={anyBusy}
            data-testid="interrupted-resume-panel-discard"
            className="w-full rounded-lg border-0 bg-transparent p-[7px] text-[12px] font-semibold text-slate-500 hover:text-slate-300 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {busy === 'discard'
              ? 'Discarding…'
              : `Discard ${lines} captured ${lines === 1 ? 'line' : 'lines'}`}
          </button>
        </div>
      </div>
    </div>
  )
}
